<template>
  <v-app
    class="lpu-services"
    :class="isMobile ? 'lpu-services_mobile' : 'lpu-services_full'"
    :data-qa="dataQA.component"
    data-lpu-services
  >
    <InvasiveServiceInfo />
    <SearchCombobox
      ref="lpu-services-search"
      class="mb-2 lpu-services-search"
      placeholder="Найти услугу"
      :data-qa="dataQA.search"
      :initial-data="serviceClassList"
      @search:default="handleSearchDefault"
      @search:text="handleSearchText"
      @search:class="handleSearchClass"
    />

    <div
      :class="{
        'd-flex justify-space-between align-center': !isMobile && isEnabledOnlineAppointment !== null,
      }"
    >
      <LpuServicesOnlineSwitch
        v-if="isEnabledOnlineAppointment !== null"
        :is-enabled-online-app="isEnabledOnlineAppointment"
        :data-qa="dataQA.switchOnlineAppointment"
        :class="{
          'my-2': isMobile,
        }"
        @lpu-services-online-switch:change="handleOnlineSwitch"
      />
      <div v-show="isVisibleInitialServiceList">
        <slot name="update-date" />
      </div>
      <div v-if="!isVisibleInitialServiceList && serviceList.length">
        <div class="d-flex justify-end">
          <div
            class="lpu-services__price-update-text text-caption mt-1"
            :data-qa="dataQA.serviceDateUpdated"
            :class="priceUpdateColorPalette"
          >
            {{ priceUpdateTextContent }}
          </div>
        </div>
      </div>
    </div>
    <div v-show="isVisibleInitialServiceList">
      <slot v-bind="{ handleClickServicePrice }" />
    </div>
    <div v-if="!isVisibleInitialServiceList && serviceList.length">
      <div
        class="lpu-services__service-list py-4"
        :data-qa="dataQA.serviceList"
      >
        <div
          v-for="(serviceClass, serviceClassIndex) in serviceList"
          :key="`service-class-${serviceClassIndex}`"
          class="lpu-services__service-group"
          :data-qa="dataQA.serviceGroup"
        >
          <div
            class="text-h6 text--text mb-6"
            :data-qa="dataQA.serviceClassName"
          >
            {{ serviceClass.name }}
          </div>
          <div
            v-for="(service, serviceIndex) in serviceClass.sublist"
            :key="`service-${serviceIndex}`"
            class="lpu-services__service"
            :data-qa="dataQA.service"
          >
            <div class="b-lpu-services__service-wrapper">
              <div
                :class="{
                  'lpu-services__service-item': !isMobile,
                }"
              >
                <div
                  class="d-inline-block lpu-services__service-name text-body-1"
                  :data-qa="dataQA.serviceName"
                >
                  {{ service.name }}

                  <div
                    v-if="service.duration"
                    class="text-body-2 text-secondary--text"
                    :data-qa="dataQA.serviceDuration"
                  >
                    {{ getDurationText(service.duration) }}
                  </div>

                  <div
                    v-if="isVisibleServiceTypes(service)"
                    class="adminka-box pa-0 mt-1"
                  >
                    {{ service.service_types.join(', ') }}
                  </div>
                  <div
                    v-if="isVisibleWorkplaces(service)"
                    class="adminka-box pa-0 mt-1"
                  >
                    <span
                      v-for="[speciality, fios] in service.workplaces"
                      :key="speciality"
                    >
                      {{ speciality }}: {{ fios.join(', ') }}<br>
                    </span>
                  </div>
                </div>
                <div
                  class="flex-shrink-0"
                  :class="[isMobile ? 'mt-3 w-100' : 'ml-auto']"
                >
                  <LpuServicesPrice
                    class="lpu-services__price"
                    :is-btn="isBtnServicePrice(service)"
                    :price="service.prices.full"
                    :has-schedule="service.hasSchedule"
                    :synonym-id="service.synonym_id"
                    @lpu-services-price:click-btn="event => {
                      handleClickServicePrice({
                        id: service.synonym_id,
                        allowTreatmentReferral: service.allow_treatment_referral,
                        requiresPrimaryConsultation: service.requires_primary_consultation,
                        hasPrimaryConsultationDoctors: service.has_primary_consultation_doctors,
                        doctorSpecialities: service.doctor_specialities,
                      }, event)
                    }"
                  />
                </div>
              </div>
              <div
                class="service-prices-schedule"
                :data-qa="dataQA.schedule"
              />
            </div>
          </div>
        </div>
        <div
          v-if="isActiveButtonShowAll"
          class="d-flex justify-center"
        >
          <v-btn
            color="bg-primary"
            class="lpu-services__show-all"
            elevation="0"
            :data-qa="dataQA.buttonShowAll"
            :block="isMobile"
          >
            <span class="primary--text">
              Показать все услуги
            </span>
          </v-btn>
        </div>
      </div>
    </div>
    <div
      v-else-if="!isVisibleInitialServiceList"
      class="text-center py-16 my-1"
      :data-qa="dataQA.serviceNotFound"
    >
      <img
        class="d-block mb-2 mx-auto"
        src="/static/_v1/pd/pictures/ui-kit/search.svg"
        width="150"
        height="150"
      >
      <span class="text--text text-body-1">
        Такой услуги нет в клинике
      </span>
    </div>
    <LpuServicesPagination
      v-show="isVisiblePagination"
      :data-qa="dataQA.pagination"
      :page-count="paginationData.total"
      :initial-page="paginationData.current"
      @relocate-page:number="handleRelocatePage"
    />
  </v-app>
</template>

<script>
import {
  scrollTo,
  getElementOffsetTop,
  setQueryParam,
  deleteQueryParam,
  transformToNumber,
  isNetworkDisconnect,
  dispatchCustomEventGlobally,
  declensionByNum,
} from 'www/utils'
import {
  emitOpenGettingCard,
  isEnabledClubServicesSwitch,
} from 'www/global.js.blocks/common/club-card-getting/functions'
import { AppointmentModules } from 'www/modules/appointment-modules'
import YaGoals from 'www/modules/YandexGoals'
import { SCHEDULE_EVENTS } from 'www/modules/Schedule/constants'
import SearchCombobox from 'www/vue_components/common/core/SearchCombobox/SearchCombobox.vue'
import LpuServicesPagination from 'www/vue_components/common/LpuServices/components/LpuServicesPagination.vue'
import LpuServicesPrice from 'www/vue_components/common/LpuServices/components/LpuServicesPrice.vue'
import LpuServicesOnlineSwitch from 'www/vue_components/common/LpuServices/components/LpuServicesOnlineSwitch.vue'
import {
  DATA_QA,
  YANDEX_GOALS,
  QUERY_PARAM_PAGE,
  DATE_UPDATED_TEXT_DATA,
  DATE_UPDATED_COLOR_PALETTE_DATA,
} from 'www/vue_components/common/LpuServices/constants'
import { getServiceList } from 'www/vue_components/common/LpuServices/api'
import { getScheduleElements } from 'www/vue_components/common/LpuServices/functions'
import useInvasiveServiceInfoStore from 'src/components/common/InvasiveServiceInfo/stores/useInvasiveServiceInfoStore'
import InvasiveServiceInfo from 'src/components/common/InvasiveServiceInfo/InvasiveServiceInfo.vue'

export default {
  name: 'LpuServices',
  components: {
    InvasiveServiceInfo,
    LpuServicesPrice,
    LpuServicesPagination,
    SearchCombobox,
    LpuServicesOnlineSwitch,
  },
  props: {
    initialServiceClassList: {
      type: Array,
      default: () => [],
      required: true,
    },
    serviceDateUpdated: {
      type: String,
      default: '',
      required: true,
    },
    apiUrl: {
      type: String,
      default: '',
      required: true,
    },
    lpuId: {
      type: Number,
      default: 0,
      required: true,
    },
    pageCount: {
      type: Number,
      default: 1,
      required: true,
    },
    initialPage: {
      type: Number,
      default: 1,
    },
    scrollToSelector: {
      type: String,
      default: '',
    },
    isEnabledLpuClub: {
      type: Boolean,
      default: false,
    },
    isEnabledSwitchProp: {
      type: Boolean,
      default: false,
    },
    priceUpdateStatusCode: {
      type: Number,
      default: 0,
    },
    townTimedelta: {
      type: Number,
      default: 0,
    },
    townTranslit: {
      type: String,
      default: '',
    },
    dateStart: {
      type: String,
      default: '',
    },
    isEnabledDirect: {
      type: Boolean,
      default: false,
    },
    isEnabledOnlineAppointment: {
      type: [Boolean, null], // null - ОЗ нет у клиники, false - у клиники < 10 услуг с ОЗ, true - у клиники > 10 услуг с ОЗ
      default: false,
    },
  },
  data() {
    return {
      isClub: false,
      serviceId: null,
      lastQueryData: {
        text: '',
        type: 'search_query',
        page: this.initialPage,
      },
      paginationData: {
        total: this.pageCount,
        current: this.initialPage,
      },
      isEnabledOnlineSwitch: this.isEnabledOnlineAppointment,
      isPreliminaryConsultation: false,
      userInClub: window.USER.club.isInClub,
      isEnabledClubSwitch: this.isEnabledSwitchProp,
      appointmentMethods: new AppointmentModules(),
      serviceList: [],
      serviceClassList: [],
      visibleInitialServiceList: true,
      visiblePagination: true,
      dataQA: DATA_QA,
      instanceYaGoals: new YaGoals(),
      wasUsedSearch: false,
      isMobile: window.MOBILE_VERSION,
      priceUpdateStatusCodeNumber: transformToNumber(this.priceUpdateStatusCode),
      scrollToParams: {
        offsetTop: 0,
        options: {
          duration: 1000,
          offset: 0,
        },
      },
      serviceData: {
        allowTreatmentReferral: false,
        requiresPrimaryConsultation: false,
        hasPrimaryConsultationDoctors: false,
        doctorSpecialities: [],
        townTranslit: undefined,
      },
    }
  },
  computed: {
    isActiveButtonShowAll() {
      return this.isEnabledOnlineSwitch
        && Number(this.paginationData.current) === Number(this.paginationData.total)
    },
    isActiveClub() {
      return this.isEnabledLpuClub
    },
    isActiveClubSwitch() {
      return this.isEnabledClubSwitch || this.userInClub
    },
    priceUpdateTextContent() {
      return DATE_UPDATED_TEXT_DATA(this.serviceDateUpdated)[this.priceUpdateStatusCodeNumber]
    },
    priceUpdateColorPalette() {
      return DATE_UPDATED_COLOR_PALETTE_DATA[this.priceUpdateStatusCodeNumber]
    },
    isVisiblePagination() {
      return this.visiblePagination && this.paginationData.total > 1
    },
    isVisibleInitialServiceList() {
      return this.visibleInitialServiceList
    },
  },
  created() {
    this.serviceClassList = this.initialServiceClassList

    this.sendYaGoals(YANDEX_GOALS.visitPage)
  },
  mounted() {
    this.initScrollToParams()

    window.addEventListener('club-discount-switch:click', this.handleClubDiscountSwitchClick)
    window.addEventListener(SCHEDULE_EVENTS.renderSuccess, this.handleRenderSuccess)
    window.addEventListener(SCHEDULE_EVENTS.redirectSynonymPrice, this.handleRedirectAppointmentV2)
    window.addEventListener(SCHEDULE_EVENTS.hideAllToggleCalendar, this.handleHideAllToggleCalendar)
  },
  methods: {
    getDurationText(duration) {
      return `${duration} ${declensionByNum(duration, ['минута', 'минуты', 'минут'])}`
    },
    isBtnServicePrice(service) {
      return service.appointment_on && service.has_intervals && service.is_available_appointment_direct
    },
    isVisibleServiceTypes(service) {
      return window.USER.isStaff
                && !this.isMobile
                && service.service_types?.length
    },
    isVisibleWorkplaces(service) {
      return window.USER.isStaff
                && !this.isMobile
                && service.workplaces
                && Object.keys(service.workplaces).length
    },
    initScrollToParams() {
      if (this.scrollToSelector) {
        const scrollToNode = document.querySelector(this.scrollToSelector)

        if (scrollToNode) {
          this.scrollToParams.offsetTop = getElementOffsetTop(scrollToNode)
          return
        }
      }

      this.scrollToParams.offsetTop = getElementOffsetTop(this.$refs['lpu-services-search'].$el)
      // 64 - высота шапки в мобильной версии, нужно, т.к. при скролле вверх появляется шапка
      this.scrollToParams.options.offset = this.isMobile ? -64 : 0
    },
    sendYaGoals(name) {
      this.instanceYaGoals.send({ name })
    },
    updateServiceClassList() {
      this.serviceClassList = this.serviceList.map(item => item.name)
    },
    async updateServiceList(postData) {
      const requestData = postData

      if (this.isEnabledOnlineSwitch) {
        requestData.is_available_appointment_direct = this.isEnabledOnlineSwitch
      }

      await getServiceList({
        url: this.apiUrl,
        data: requestData,
      })
        .then(({ data }) => {
          this.paginationData.total = data.num_pages
          this.paginationData.current = data.current_page
          this.visiblePagination = true
          this.visibleInitialServiceList = true

          this.updateUrl()

          this.serviceList = data.data.map(item => {
            item.sublist = item.sublist.map(i => {
              i.hasSchedule = false
              return i
            })

            return item
          })
        })
        .catch(({ message, response } = {}) => {
          const { status } = response || {}

          if (isNetworkDisconnect({ status, message })) {
            return
          }

          if (status === 404) {
            this.visiblePagination = false
            this.visibleInitialServiceList = false
          }
        })

      this.visibleInitialServiceList = false
    },
    updateUrl() {
      deleteQueryParam(QUERY_PARAM_PAGE)
      setQueryParam(QUERY_PARAM_PAGE, this.paginationData.current)
    },
    handleSearchDefault() {
      this.lastQueryData = {
        text: '',
        type: 'search_query',
        page: 1,
      }

      this.handleOnlineSwitch({ val: this.isEnabledOnlineSwitch })
    },
    async handleSearchText(queryText) {
      if (!this.wasUsedSearch) {
        this.sendYaGoals(YANDEX_GOALS.useSearch)
        this.wasUsedSearch = true
      }

      this.lastQueryData = {
        text: queryText,
        type: 'search_query',
        page: this.paginationData.current,
      }

      const payload = {
        search_query: queryText,
        lpu_id: this.lpuId,
      }

      await this.updateServiceList(payload)
    },
    async handleSearchClass(queryClass) {
      if (!this.wasUsedSearch) {
        this.sendYaGoals(YANDEX_GOALS.useSearch)
        this.wasUsedSearch = true
      }

      this.lastQueryData = {
        text: queryClass,
        type: 'service_category_name',
        page: this.paginationData.current,
      }

      const payload = {
        service_category_name: queryClass,
        lpu_id: this.lpuId,
      }

      await this.updateServiceList(payload)
    },
    async handleRelocatePage(queryPage) {
      this.paginationData.current = queryPage

      const payload = {
        page: queryPage,
        lpu_id: this.lpuId,
      }

      payload[this.lastQueryData.type] = this.lastQueryData.text

      await this.updateServiceList(payload)

      scrollTo(this.scrollToParams.offsetTop, this.scrollToParams.options)
    },
    handleRedirectAppointment() {
      const appointmentData = {
        lpu: this.lpuId,
        misServiceId: this.serviceId,
      }

      const isClubModal = isEnabledClubServicesSwitch() && this.isEnabledLpuClub && this.isClub

      if (this.isPreliminaryConsultation) {
        dispatchCustomEventGlobally('preliminary-consultation-modal:updated', {
          isVisible: true,
          handleInvasiveModalContinue: () => {
            dispatchCustomEventGlobally('preliminary-consultation-modal:updated', { isVisible: false })

            if (isClubModal) {
              emitOpenGettingCard({ appointmentData })
            } else {
              this.appointmentMethods.redirectToNewAppointmentClinic(appointmentData)
            }
          },
        })

        return
      }

      if (isClubModal) {
        emitOpenGettingCard({ appointmentData })

        return
      }

      this.appointmentMethods.redirectToNewAppointmentClinic(appointmentData)
    },
    handleClickServicePrice(serviceData, event) {
      try {
        this.sendYaGoals(YANDEX_GOALS.clickServicePriceButton)

        this.serviceId = serviceData.id
        this.serviceData = {
          ...serviceData,
          townTranslit: this.townTranslit,
        }

        const scheduleElements = getScheduleElements(event)

        if (scheduleElements) {
          dispatchCustomEventGlobally(SCHEDULE_EVENTS.clickSynonymPrice, {
            scheduleElements,
            lpuId: this.lpuId,
            synonymId: serviceData.id,
            dateStart: this.dateStart,
            isLpuPriceList: true,
            townTimedelta: this.townTimedelta,
            isEnabledDirect: this.isEnabledDirect,
            isEnabledLpuClub: false,
            serviceData: this.serviceData,
          })

          return
        }

        this.handleRedirectAppointmentV2()
      } catch { /* Empty */ }
    },
    handleRedirectAppointmentV2() {
      const appointmentData = {
        lpu: this.lpuId,
        misServiceId: this.serviceId,
        byReferral: undefined,
      }

      if (
        this.serviceData.allowTreatmentReferral
        || (this.serviceData.requiresPrimaryConsultation && this.serviceData.hasPrimaryConsultationDoctors)
      ) {
        const invasiveServiceInfoStore = useInvasiveServiceInfoStore()

        if (window.invasiveServiceInfoStore) {
          window.invasiveServiceInfoStore.push({ store: invasiveServiceInfoStore, name: 'LpuServices' })
        } else {
          window.invasiveServiceInfoStore = [{ store: invasiveServiceInfoStore, name: 'LpuServices' }]
        }

        invasiveServiceInfoStore.openInfoModal({
          requiresPrimaryConsultation: this.serviceData.requiresPrimaryConsultation,
          hasPrimaryConsultationDoctors: this.serviceData.hasPrimaryConsultationDoctors,
          allowTreatmentReferral: this.serviceData.allowTreatmentReferral,
          doctorSpecialities: this.serviceData.doctorSpecialities,
          townTranslit: this.townTranslit,
          handleConfirmReferral: () => {
            appointmentData.byReferral = 'true'
            this.appointmentMethods.redirectToNewAppointmentClinic(appointmentData)
          },
          handleAppointment: () => {
            this.appointmentMethods.redirectToNewAppointmentClinic(appointmentData)
          },
        })

        return
      }

      this.appointmentMethods.redirectToNewAppointmentClinic(appointmentData)
    },
    handleClubDiscountSwitchClick({ detail }) {
      this.isEnabledClubSwitch = detail.isEnabled
    },
    handleRenderSuccess({ detail }) {
      const { synonymId } = detail

      this.serviceList = this.serviceList.map(item => {
        item.sublist = item.sublist.map(i => {
          i.hasSchedule = i.synonym_id === synonymId
          return i
        })

        return item
      })
    },
    handleHideAllToggleCalendar() {
      this.serviceList = this.serviceList.map(item => {
        item.sublist = item.sublist.map(i => {
          i.hasSchedule = false
          return i
        })

        return item
      })
    },
    async handleOnlineSwitch({ val }) {
      const { text, type } = this.lastQueryData

      this.isEnabledOnlineSwitch = val

      await this.updateServiceList({
        lpu_id: this.lpuId,
        [type]: text,
      })
    },
  },
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/mixins';

#app[data-lpu-services] {
  @include v-app-reset;
}
</style>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

$text-margin-right: 24px;

.b-lpu-services {
  &__service-wrapper {
    width: 100%;
  }
}

.lpu-services {
  $block: &;

  &__service-list {
    overflow-wrap: break-word;
  }

  &__service,
  &__service-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-height: 40px;
  }

  &__service-name {
    color: $ui-kit-text;
    margin-right: $text-margin-right;
  }

  &__price-update-text {
    padding: 4px 8px;
    border-radius: $border-radius-sm;
  }

  &_mobile {
    $margin-side: 16px;

    #{$block}__service-group:not(:last-child) {
      margin-bottom: $margin-side * 2;
    }

    #{$block}__service {
      padding-bottom: $margin-side;
      padding-top: $margin-side;
      flex-wrap: wrap;
    }

    #{$block}__service:not(:last-child) {
      border-bottom: 1px solid $ui-kit-bg-gray-60;
    }
  }

  &_full {
    $margin-side: 24px;

    #{$block}__service-group:not(:last-child) {
      margin-bottom: $margin-side * 2;
    }

    #{$block}__service:not(:last-child) {
      margin-bottom: $margin-side;
    }
  }
}
</style>
